// frontend/src/components/QuestionComponent.jsx
import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function QuestionComponent({ question, chapterId, contentBlockId, existingAnswer, onAnswerChange }) {
  // contentBlockId is now contentBlockIndex (number) instead of _id (string)
  // Track the last contentBlockId to detect when we've moved to a different block
  const lastContentBlockId = useRef(null);
  
  // Initialize answer state - will be updated by useEffect when existingAnswer changes
  const [answer, setAnswer] = useState(() => {
    // Preserve existing answer when component mounts
    if (question.type === 'checkbox' || question.questionType === 'checkbox') {
      return Array.isArray(existingAnswer)
        ? existingAnswer
        : existingAnswer
        ? [existingAnswer]
        : [];
    }
    return existingAnswer !== undefined && existingAnswer !== null ? existingAnswer : '';
  });
  
  // Update answer state when existingAnswer prop changes OR when navigating to a different block
  // This ensures answers are auto-inserted when reviewing completed chapters and navigating between blocks
  useEffect(() => {
    const isCheckbox = question.type === 'checkbox' || question.questionType === 'checkbox';
    
    // Always sync answer state with existingAnswer prop
    // This handles both block changes and answer updates
    if (existingAnswer !== undefined && existingAnswer !== null) {
      if (isCheckbox) {
        const newAnswer = Array.isArray(existingAnswer)
          ? existingAnswer
          : existingAnswer
          ? [existingAnswer]
          : [];
        const currentAnswerStr = JSON.stringify(answer);
        const newAnswerStr = JSON.stringify(newAnswer);
        
        // Update if different
        if (currentAnswerStr !== newAnswerStr) {
          setAnswer(newAnswer);
        }
      } else {
        // For multiple choice and short answer, update if different
        const currentAnswerStr = JSON.stringify(answer);
        const existingAnswerStr = JSON.stringify(existingAnswer);
        
        if (currentAnswerStr !== existingAnswerStr) {
          setAnswer(existingAnswer);
        }
      }
    } else {
      // existingAnswer is undefined/null - clear the answer only if it's not already empty
      if (isCheckbox) {
        if (answer.length > 0) {
          setAnswer([]);
        }
      } else {
        if (answer !== '') {
          setAnswer('');
        }
      }
    }
    
    // Update the last content block ID tracker
    lastContentBlockId.current = contentBlockId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAnswer, contentBlockId, question.type, question.questionType]); // Depend on existingAnswer, contentBlockId, and question type
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [saveStatus, setSaveStatus] = useState(''); // For displaying "Saving..." or "Saved."
  const firstRender = useRef(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const saveAnswer = async () => {
    try {
      setIsSubmitting(true);
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/user/submit-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        // Send contentBlockIndex (contentBlockId prop is now the index number)
        body: JSON.stringify({ chapterId, contentBlockIndex: contentBlockId, answer }),
      });

      if (!res.ok) throw new Error('Failed to save answer');
      setSaveStatus('Saved.');
    } catch (err) {
      setSaveStatus('Error saving answer');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track the last saved answer to prevent unnecessary saves
  const lastSavedAnswer = useRef(null);
  
  // Autosave effect - save answers when they change (works for both resume and review)
  useEffect(() => {
    // Skip the effect on the first render to avoid saving empty answers
    if (firstRender.current) {
      firstRender.current = false;
      // Store the initial answer as the last saved answer
      lastSavedAnswer.current = answer;
      // Don't save on first render - answers are already saved in the database
      // Only update parent state if needed
      if (onAnswerChange && answer !== undefined && answer !== null && answer !== '') {
        onAnswerChange(contentBlockId, answer);
      }
      return;
    }
    
    // Don't save if answer is empty/null/undefined
    if (answer === undefined || answer === null || answer === '') return;
    
    // Don't save if answer hasn't actually changed from what we last saved
    const answerStr = JSON.stringify(answer);
    const lastSavedStr = JSON.stringify(lastSavedAnswer.current);
    if (answerStr === lastSavedStr) {
      return; // Answer hasn't changed, don't save
    }

    // Update parent's state using the contentBlockId
    // The parent (Chapter.updateAnswer) will handle the server save
    // This prevents duplicate API calls
    if (onAnswerChange) {
      onAnswerChange(contentBlockId, answer);
    }

    // Update last saved answer
    lastSavedAnswer.current = answer;

    // Don't call saveAnswer() here - let the parent handle it via onAnswerChange
    // This prevents duplicate API calls and race conditions
    setSaveStatus('Saving...');
    
    // Clear status after a delay to show it was saved
    const timer = setTimeout(() => {
      setSaveStatus('Saved.');
      setTimeout(() => setSaveStatus(''), 2000);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer]); // Only depend on answer to trigger on changes

  // Render 'multiple_choice' question
  if (question.type === 'multiple_choice' || question.questionType === 'multiple_choice') {
    // Calculate dynamic font size based on number of options
    const optionCount = question.options?.length || 0;
    const baseFontSize = 24; // 3xl = 24px
    const minFontSize = 16; // Minimum readable size
    const maxOptionsForBaseSize = 4;
    const dynamicFontSize = optionCount > maxOptionsForBaseSize 
      ? Math.max(minFontSize, baseFontSize - Math.floor((optionCount - maxOptionsForBaseSize) * 2))
      : baseFontSize;
    
    return (
      <div className="font-body flex flex-col h-full max-h-[80vh]"> {/* Base font style with flex layout */}
        <p className="font-body mb-4 text-gray-800 dark:text-gray-200" style={{ fontSize: `${Math.min(dynamicFontSize + 4, 28)}px` }}>{question.prompt}</p>
        <ul className="list-none space-y-2 flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(100vh - 200px)' }}> {/* Scrollable container */}
          {question.options.map((option, idx) => {
            // Use strict equality and also check string comparison for safety
            const isSelected = answer === option || String(answer) === String(option);
            
            return (
              <li key={idx}>
                <label
                  className={`
                    flex items-center p-4 border rounded-lg cursor-pointer
                    transition-all duration-150 ease-in-out
                    focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#51c0a6]
                    ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}
                    ${isSelected // For radio, answer is a single value
                      ? 'bg-[#51c0a6] dark:bg-[#479482] border-[#51c0a6] dark:border-[#479482] text-white shadow-md'
                      : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name={`question_${contentBlockId}`}
                    value={option}
                    checked={isSelected}
                    onChange={() => !isSubmitting && setAnswer(option)}
                    disabled={isSubmitting}
                    className="sr-only"
                  />
                  <span className={`
                    flex-shrink-0 w-5 h-5 mr-4 border-2 rounded-full flex items-center justify-center
                    ${isSelected ? 'border-white bg-white' : 'border-gray-400 bg-white'}
                  `}>
                    {isSelected && (
                      <span className="w-2.5 h-2.5 bg-[#51c0a6] rounded-full"></span>
                    )}
                  </span>
                  <span style={{ fontSize: `${dynamicFontSize}px` }}>
                    {option}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        <p className="text-sm mt-8 h-5 text-gray-500">{saveStatus || (isSubmitting ? 'Saving...' : '')}</p>
      </div>
    );
  }

  // Render 'checkbox' question
  if (question.type === 'checkbox' || question.questionType === 'checkbox') {
    const handleCheckboxChange = (option) => {
      setAnswer((prev) => {
        const newAnswer = Array.isArray(prev) ? [...prev] : [];
        if (newAnswer.includes(option)) {
          return newAnswer.filter((o) => o !== option);
        } else {
          newAnswer.push(option);
          return newAnswer;
        }
      });
    };

    // Calculate dynamic font size based on number of options
    const optionCount = question.options?.length || 0;
    const baseFontSize = 24; // 3xl = 24px
    const minFontSize = 16; // Minimum readable size
    const maxOptionsForBaseSize = 4;
    const dynamicFontSize = optionCount > maxOptionsForBaseSize 
      ? Math.max(minFontSize, baseFontSize - Math.floor((optionCount - maxOptionsForBaseSize) * 2))
      : baseFontSize;

    return (
      <div className="font-body flex flex-col h-full"> {/* Base font style with flex layout */}
        <p className="font-body mb-4 text-gray-800 dark:text-gray-200" style={{ fontSize: `${Math.min(dynamicFontSize + 4, 28)}px` }}>{question.prompt}</p>
        <ul className="list-none space-y-2 flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(100vh - 200px)' }}> {/* Scrollable container */}
          {question.options.map((option, idx) => {
            const isChecked = Array.isArray(answer) && answer.includes(option);
            return (
              <li key={idx}>
                <label
                  className={`
                    flex items-center p-4 border rounded-lg cursor-pointer
                    transition-all duration-150 ease-in-out
                    focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#51c0a6]
                    ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}
                    ${isChecked
                      ? 'bg-[#51c0a6] border-[#51c0a6] text-white shadow-md' // Selected state
                      : 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700 hover:border-gray-400' // Default state
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    value={option}
                    checked={isChecked}
                    onChange={() => !isSubmitting && handleCheckboxChange(option)}
                    disabled={isSubmitting}
                    className="sr-only" // Visually hide the default checkbox
                  />
                  {/* Custom checkbox visual indicator */}
                  <span className={`
                    flex-shrink-0 w-5 h-5 mr-4 border-2 rounded flex items-center justify-center
                    ${isChecked ? 'border-white bg-white' : 'border-gray-400 bg-white'}
                  `}>
                    {isChecked && (
                      // Checkmark icon (simple SVG or a character)
                      <svg className="w-3 h-3 text-[#51c0a6]" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </span>
                  <span style={{ fontSize: `${dynamicFontSize}px` }}> {/* Option text with dynamic sizing */}
                    {option}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        {/* Show save status */}
        <p className="text-sm mt-4 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0">{saveStatus || (isSubmitting ? 'Saving...' : '')}</p>
      </div>
    );
  }

  // Render 'short_answer' question
  if (question.type === 'short_answer' || question.questionType === 'short_answer' || !question.type) {
    return (
      <div className="font-body h-full flex flex-col"> {/* Base font style */}
        <p className="font-body text-3xl mb-6 text-gray-800 dark:text-gray-200">{question.prompt}</p>
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          className={`
            w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg 
            font-body text-3xl text-gray-700 dark:text-gray-200
            bg-white dark:bg-gray-800
            transition-colors duration-150 ease-in-out
            hover:border-gray-400 dark:hover:border-gray-500
            focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white
      `}
          rows={8} // Keep or adjust as needed
          placeholder="Type your answer here..."
        />
        <div className="text-sm mt-8 h-5 text-gray-500 dark:text-gray-400"> {/* Consistent save status styling */}
          {saveStatus || (isSubmitting ? 'Saving...' : '')}
        </div>
      </div>
    );
  }

  return null;
}

export default QuestionComponent;
